import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceStatus,
  IExperience,
  IExperienceTemplate,
  ISupportingActor,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Divider,
  IconButton,
  List,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@material-ui/core';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { Padding } from '.';
import { TabPanel } from '../../../../../components/Tabs';
import { useHover } from '../../../../../hooks/useHover';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../services/currentUser';
import {
  groupConcreteMessages,
  moveAutomationToToday,
  moveMessageToToday,
  moveTaskToToday,
  openSubTask,
  skipAutomation,
  skipAutomationAction,
  skipMessage,
  skipSubTask,
  skipTask,
  unskipAutomation,
  unskipAutomationAction,
  unskipMessage,
  unskipTask
} from '../../../../../services/experiences/instance';
import { getTemplateDuration } from '../../../../../services/experiences/template';
import { _mapFn } from '../../../../../services/participatDialog/participantDialogHelpers';
import { InterpolationDefaultOptions } from '../../../../../services/set/interpolation';
import { ONE_DAY } from '../../../../../services/time';
import { toFullName } from '../../../../../services/user';
import { UserTaskTimeline } from '../../../../Users/components/UserTaskTimeline';
import { useActorDescriptorsFromTemplate } from '../../../components/Actors';
import { AnchorField } from '../../../components/Anchors';
import { getModifications } from '../modifications';
import { LaunchSuggestion, SendManualReminder } from '../ParticipantDialog';
import { Automation } from '../ParticipantDialogComponents/Automation';
import { TaskAndAutomationList } from '../ParticipantDialogComponents/CommonStyledComponents';
import { Message } from '../ParticipantDialogComponents/Message';
import { Task } from '../ParticipantDialogComponents/Task';

const USE_MANUAL_REMINDERS = false;
const TAB = 0;

type Participant = {
  key: string;
  user: Doc<IUser>;
  missingActors: ISupportingActor[];
};

export type ListItem = {
  participant: Participant;
  instance: {
    id: string;
    collection: string;
    data: IExperience;
  };
  actors: { [key: string]: Doc<IUser> };
};

const ContentLayout = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  height: '100%',
  maxHeight: '100%'
}));

const SelectedParticipantsContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflowY: 'scroll',
  width: '20%',
  maxWidth: '20%'
}));

const ParticipantDialogContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  flexGrow: 1,
  flexDirection: 'column',
  overflowY: 'scroll',
  width: '80%',
  maxWidth: '80%'
}));

const ListItemContainer = styled('div')<{ selected?: boolean }>((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(3)}px`,
  backgroundColor: p.selected
    ? p.theme.custom.colors.blue.w100
    : p.theme.custom.colors.gray.white,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: p.selected
      ? p.theme.custom.colors.blue.w200
      : p.theme.custom.colors.gray.w100
  }
}));

const SelectUserStateContainer = styled('div')((p) => ({
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%'
}));

const ListItemText = ({
  actor,
  variant
}: {
  actor: Participant;
  variant: 'normal' | 'warning';
}) => {
  const theme = useTheme();
  return (
    <FlexContainer spacing={1} justifyContent="center">
      <Typography
        variant={variant === 'normal' ? 'body1' : 'h6'}
        style={{
          color:
            variant === 'warning' ? theme.custom.colors.yellow.w700 : 'black'
        }}
      >
        {toFullName(actor.user.data)}
      </Typography>
      {variant === 'warning' && (
        <AlertTriangle size={16} color={theme.custom.colors.yellow.w700} />
      )}
    </FlexContainer>
  );
};

const SelectableListItemWithHover = ({
  selected,
  left,
  right,
  onClick
}: {
  selected: boolean;
  left: React.ReactNode;
  right: React.ReactNode;
  onClick: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const hovered = useHover(ref);

  return (
    <ListItemContainer ref={ref} onClick={onClick} selected={selected}>
      {left && left}
      {hovered && right}
    </ListItemContainer>
  );
};

const PreviewAndConfigure = ({
  originalD,
  d,
  tpl,
  setD,
  anchorFields,
  participant,
  actors,
  missingSupportingActors
}: {
  originalD: Doc<IExperience>;
  d: Doc<IExperience>;
  setD: (nextInstance: Doc<IExperience>) => void;
  tpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  participant: Doc<IUser>;
  actors: { [actorKey: string]: Doc<IUser> };
  missingSupportingActors: ISupportingActor[];
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { customUserFields } = org;
  const duration = getTemplateDuration(tpl);
  const hasZoomControls = duration > ONE_DAY * 21;

  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );
  const modifications = useMemo(() => getModifications(originalD, d), [
    originalD,
    d
  ]);

  const interpolationOptions: InterpolationDefaultOptions = useMemo(
    () => ({
      dateFormat: org.dateFormats.MMDDYYYY
    }),
    [org.dateFormats.MMDDYYYY]
  );

  const v = useMemo(() => {
    return _mapFn(
      tpl,
      d,
      participant,
      {},
      actors,
      customUserFields,
      org,
      actorDescriptors,
      interpolationOptions
    );
  }, [
    d,
    tpl,
    customUserFields,
    org,
    actorDescriptors,
    interpolationOptions,
    participant,
    actors
  ]);

  const isMessageModified = (id: string) => {
    return !!modifications.messages[id]?.changed;
  };

  const isAutomationActionModified = (
    automationId: string,
    actionId: string
  ) => {
    return !!modifications.automations[automationId]?.actions[actionId]
      ?.changed;
  };

  const isAutomationModified = (automationId: string) => {
    return !!modifications.automations[automationId]?.changed;
  };

  const isTaskModified = (taskId: string) => {
    return !!modifications.tasks[taskId]?.changed;
  };

  const isSubTaskModified = (taskId: string, subTaskId: string): boolean => {
    return !!modifications.tasks[taskId]?.subTasks[subTaskId]?.changed;
  };

  // This will always be empty in this case
  const groupedConcreteMessages = useMemo(
    () => groupConcreteMessages(d.data.messages),
    [d.data.messages]
  );

  return (
    <>
      {v && (
        <>
          {d.data.status === ExperienceStatus.SUGGESTED && (
            <LaunchSuggestion d={d} user={v.user} />
          )}
          <UserTaskTimeline
            d={d}
            tpl={tpl}
            variant="normal"
            onChange={setD}
            anchorFields={anchorFields}
            hasZoomControls={hasZoomControls}
            missingSupportingActors={missingSupportingActors}
            isInEnrollMode={true}
          />
          {d.data.status === ExperienceStatus.LATE && USE_MANUAL_REMINDERS && (
            <SendManualReminder d={d} user={v.user} />
          )}
          <Tabs value={TAB} indicatorColor="primary" textColor="primary">
            <Tab value={0} label={t('Experience Status')} wrapped />
          </Tabs>
          <TabPanel active={TAB === 0}>
            <TaskAndAutomationList>
              {v.items.map((item) => (
                <>
                  {item.type === 'TASK' && (
                    <Task
                      key={`task-${item.data.id}`}
                      tpl={tpl}
                      task={item.data}
                      actor={item.actor}
                      audience={item.audience}
                      channels={item.channels}
                      orgId={d.data.orgId}
                      customizedTiming={item.customizedTiming}
                      isSelectable={false}
                      onSkipTask={() => setD(skipTask(tpl, d, item.data.id))}
                      onSkipSubTask={(subTaskId) =>
                        setD(skipSubTask(tpl, d, item.data.id, subTaskId))
                      }
                      onSkipLastSubTask={async () => {}}
                      onUnSkipTask={() =>
                        setD(unskipTask(tpl, d, item.data.id))
                      }
                      onOpenSubTask={(subTaskId) =>
                        setD(openSubTask(tpl, d, item.data.id, subTaskId))
                      }
                      isModified={isTaskModified(item.data.id)}
                      isSubtaskModified={isSubTaskModified}
                      onSendToday={() =>
                        setD(moveTaskToToday(tpl, d, item.data.id))
                      }
                      triggeredBy={item.triggeredBy}
                      onRestartDocusignSubTask={null}
                    />
                  )}
                  {item.type === 'AUTOMATION' && (
                    <Automation
                      key={`aut-${item.data.id}`}
                      a={item.data}
                      audience={item.audience}
                      customizedTiming={item.customizedTiming}
                      onReopenAutomation={() => null}
                      onForceRunAutomationAction={async () => null}
                      onForceRunAutomationActions={async () => null}
                      onSkipAutomation={() =>
                        setD(skipAutomation(d, item.data.templateId))
                      }
                      onUnskipAutomation={() =>
                        setD(unskipAutomation(d, item.data.templateId))
                      }
                      onSkipAction={(acTplId) =>
                        setD(
                          skipAutomationAction(d, item.data.templateId, acTplId)
                        )
                      }
                      onUnskipAction={(acTplId) =>
                        setD(
                          unskipAutomationAction(
                            d,
                            item.data.templateId,
                            acTplId
                          )
                        )
                      }
                      checkIfActionModified={(aId: string, acId: string) =>
                        isAutomationActionModified(aId, acId)
                      }
                      isSelectable={false}
                      onRunToday={() =>
                        setD(
                          moveAutomationToToday(tpl, d, item.data.templateId)
                        )
                      }
                      isModified={isAutomationModified(item.data.templateId)}
                      supportingActors={tpl.data.supportingActors}
                      triggeredBy={item.triggeredBy}
                    />
                  )}
                  {item.type === 'MESSAGE' && (
                    <Message
                      key={`msg-${item.data.templateId}`}
                      msgId={item.data.templateId}
                      msg={item.data}
                      actor={item.actor}
                      audience={item.audience}
                      channels={item.channels}
                      experienceId={d.id}
                      orgId={d.data.orgId}
                      customizedTiming={item.customizedTiming}
                      isSelectable={false}
                      onSuccessfulResend={async () => null}
                      onSkipMessage={() =>
                        setD(skipMessage(d, item.data.templateId))
                      }
                      onUnskipMessage={() =>
                        setD(unskipMessage(d, item.data.templateId))
                      }
                      onSendToday={() =>
                        setD(moveMessageToToday(tpl, d, item.data.templateId))
                      }
                      concreteMessages={
                        groupedConcreteMessages.byMessage[
                          item.data.templateId
                        ] || []
                      }
                      isModified={isMessageModified(item.data.templateId)}
                      triggeredBy={item.triggeredBy}
                    />
                  )}
                </>
              ))}
            </TaskAndAutomationList>
          </TabPanel>
        </>
      )}
    </>
  );
};

export const PreviewAndConfigureDialogContent = ({
  tpl,
  anchorFields,
  originalInstances,
  editableInstances,
  setEditableInstances
}: {
  tpl: Doc<IExperienceTemplate>;
  anchorFields: AnchorField[];
  originalInstances: ListItem[];
  editableInstances: ListItem[];
  setEditableInstances: React.Dispatch<React.SetStateAction<ListItem[]>>;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const [selectedUserInstanceId, setSelectedUserInstanceId] = useState('');

  const updateInstance = (d: Doc<IExperience>) => {
    setEditableInstances((prev) => {
      return prev.map((a) => {
        if (a.participant.user.id === selectedUserInstanceId) {
          a.instance = d;
          return a;
        }
        return a;
      });
    });
  };

  const getInstanceMissingActors = (listItem: ListItem) => {
    const actorKeys = listItem.instance.data.actors.map((a) => a.key);
    return listItem.participant.missingActors.filter(
      (a) => !actorKeys.includes(a.key) && !a.ignoreWhenMissing
    );
  };

  const originalInstance =
    originalInstances.find(
      (a) => a.participant.user.id === selectedUserInstanceId
    ) || undefined;

  const editableInstance =
    editableInstances.find(
      (a) => a.participant.user.id === selectedUserInstanceId
    ) || undefined;

  return (
    <ContentLayout>
      <SelectedParticipantsContainer>
        <List disablePadding>
          {editableInstances.map((s) => {
            const participant = s.participant;
            const isWarning = getInstanceMissingActors(s).length > 0;

            return (
              <SelectableListItemWithHover
                key={participant.user.id}
                selected={participant.user.id === selectedUserInstanceId}
                onClick={() => setSelectedUserInstanceId(participant.user.id)}
                left={
                  <ListItemText
                    variant={isWarning ? 'warning' : 'normal'}
                    actor={participant}
                  />
                }
                right={
                  <Tooltip title={`${t('Remove')}`}>
                    <IconButton
                      onClick={() =>
                        setEditableInstances((prev) =>
                          prev.filter((i) => i.instance.id !== s.instance.id)
                        )
                      }
                    >
                      <Trash2
                        size={20}
                        className={css`
                          &:hover {
                            color: ${theme.custom.colors.blue.w500};
                          }
                        `}
                      />
                    </IconButton>
                  </Tooltip>
                }
              />
            );
          })}
        </List>
      </SelectedParticipantsContainer>
      <Divider orientation="vertical" flexItem light />
      <ParticipantDialogContainer>
        {editableInstance && originalInstance && (
          <Padding>
            <PreviewAndConfigure
              originalD={originalInstance.instance}
              d={editableInstance.instance}
              participant={editableInstance.participant.user}
              setD={(d) => updateInstance(d)}
              actors={{
                ...editableInstance.actors,
                [PARTICIPANT_ACTOR_KEY]: editableInstance.participant.user
              }}
              tpl={tpl}
              anchorFields={anchorFields}
              missingSupportingActors={getInstanceMissingActors(
                editableInstance
              )}
            />
          </Padding>
        )}
        {!editableInstance && (
          <SelectUserStateContainer>
            <Typography variant="h4" color="textSecondary">
              {t('Select a user to customize their experience')}
            </Typography>
          </SelectUserStateContainer>
        )}
      </ParticipantDialogContainer>
    </ContentLayout>
  );
};
